import { useEffect } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { VendorContactFormField } from 'contacts/elements';
import { Form, FormField } from 'core/components';
import { NON_EMPTY } from 'core/constants';
import { InputType, NotificationPosition } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { useFocusInputFunctionality } from 'core/hooks';
import { ICorporateUserAutocomplete } from 'corporate/types';
import { ProjectFormField } from 'projects/components';
import { QuoteEntityType } from 'quotes/enums';
import { IImportVendorQuoteForm } from 'quotes/types';

import { ImportVendorLocationField } from './ImportVendorLocationField';
import { useMappedContactHelperTexts } from './useMappedContactHelperTexts';
import { getVendorContactLabel, VENDOR_QUOTE_REVISION_MAX_LENGTH } from './utils';

interface IProps {
  disabled?: boolean;
  isContactLocked?: boolean;
  isVendorLocked?: boolean;
  isVendorMapped?: boolean;
  isVendorMappingRemembered?: boolean;
  isOpen?: boolean;
  onClose?: (reset: UseFormReset<IImportVendorQuoteForm>) => void;
  onSubmit?: (
    reset: UseFormReset<IImportVendorQuoteForm>
  ) => (data: IImportVendorQuoteForm) => void;
  showJobName?: boolean;
  showProject?: boolean;
  showVendorLocation?: boolean;
  showVendorQuoteNumber?: boolean;
  // The extracted number, shown but not offered for change.
  isVendorQuoteNumberLocked?: boolean;
  showVendorQuoteRevision?: boolean;
}

// Unresolved new imports show the vendor contact picker (+ job name when no context defaulted
// it); every other field is defaulted under the hood (project = context or the BE-resolved
// quote_data.project_id) and submitted as-is.
const ImportVendorQuoteFormView = ({
  disabled = false,
  isContactLocked = false,
  isVendorLocked = false,
  isVendorMapped = false,
  isVendorMappingRemembered = false,
  isOpen = false,
  onClose,
  onSubmit,
  showJobName = false,
  showProject = false,
  showVendorLocation = false,
  showVendorQuoteNumber = false,
  isVendorQuoteNumberLocked = false,
  showVendorQuoteRevision = false,
}: IProps) => {
  const { t } = useTranslation();
  const { user } = useAuthUser();
  const { setFocusElementName } = useFocusInputFunctionality();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ user });
  const contactHelperTexts = useMappedContactHelperTexts({ isContactLocked, isVendorMapped });
  // Form's first-field focus ignores `disabled`, so it would stop on the locked contact instead of
  // the one field this prompt asks for.
  const isVendorQuoteNumberFocused =
    isContactLocked && showVendorQuoteNumber && !isVendorQuoteNumberLocked;

  // Alternate users see the resolved vendor AP (the one import persists);
  // everyone else — and alternate users with no resolvable vendor — see none.
  const getLabel = (option: ICorporateUserAutocomplete) =>
    getVendorContactLabel(option, isAlternateVersionEnabled);

  useEffect(() => {
    if (isOpen && isVendorQuoteNumberFocused) {
      setFocusElementName('externalNumber');
    }
  }, [isOpen, isVendorQuoteNumberFocused, setFocusElementName]);

  return (
    <Form
      title={t('titleImportVendorQuote')}
      isOpen={isOpen}
      onClose={onClose}
      submitBtnLabel={t('buttonLabelImportVendorQuote')}
      cancelBtnLabel={t('buttonLabelCancel')}
      onSubmit={onSubmit}
      // The finalize mutations report at the caller's position or DRAWER; a FORM slot (Form's
      // default) leaves a finalize 422 unrendered on the surfaces that pass no position, so the
      // user retried against an invisible error. DRAWER also matches the finalize's loading, so
      // Save is disabled for the duration of the attempt.
      notificationPosition={NotificationPosition.DRAWER}
      disableAutoClose
      disableAutoFocus={isVendorQuoteNumberFocused}
    >
      {/* Vendor first, then the contact: picking a vendor pre-selects that site's contact. */}
      {(showVendorLocation || isVendorMapped) && (
        <ImportVendorLocationField
          // Form focuses the first child carrying a `name`; a locked (disabled) vendor passes the
          // focus on to the contact.
          name={isVendorLocked || isVendorMapped ? undefined : 'vendorLocation'}
          // `disabled` fixes the quote's own fields on a re-import, where the vendor is the one
          // thing the prompt asks for — so it locks the contact here, never the vendor.
          isContactLocked={disabled}
          isVendorLocked={isVendorLocked}
          isVendorMapped={isVendorMapped}
          // This modal is mounted once per import, always with the BE prefill in the contact field.
          isContactPrefilled
          isVendorMappingRemembered={isVendorMappingRemembered}
        />
      )}

      <VendorContactFormField
        name="contact"
        label={t('labelVendorQuoteContact', { entityType: QuoteEntityType.QUOTE })}
        requestPayload={{
          filters: {
            // Alternate users: restrict the picker to structurally-valid vendor contacts — those
            // whose billing address resolves an AP (the same chain import persists), so every
            // offered option is import-resolvable. NON_EMPTY = the BE sentinel `(non-empty)`.
            ...(isAlternateVersionEnabled && { billToAddressAccountsPayableId: NON_EMPTY }),
          },
        }}
        getLabel={getLabel}
        showAccountsPayableId={isAlternateVersionEnabled}
        helperTexts={contactHelperTexts}
        required
        disabled={disabled || isContactLocked}
      />

      {/* Only on a new quote, where `disabled` is never set; locked once already extracted. */}
      {showVendorQuoteNumber && (
        <FormField
          name="externalNumber"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelVendorQuoteNumber'),
            helperTexts: isVendorQuoteNumberLocked ? [] : [t('messageVendorQuoteImportNumber')],
            required: !isVendorQuoteNumberLocked,
            disabled: isVendorQuoteNumberLocked,
          }}
        />
      )}

      {showVendorQuoteRevision && (
        <FormField
          name="revisionNumber"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelVendorQuoteRevision'),
            disabled,
            slotProps: { input: { maxLength: VENDOR_QUOTE_REVISION_MAX_LENGTH } },
          }}
        />
      )}

      {showProject && <ProjectFormField disabled={disabled} />}

      {showJobName && (
        <FormField
          name="jobName"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelJobName'),
            required: true,
            disabled,
          }}
        />
      )}
    </Form>
  );
};

export default ImportVendorQuoteFormView;
